import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { NovaTransferencia } from './NovaTransferencia'
import { ImportarExtratoCsv } from './ImportarExtratoCsv'
import { ExtratoAsaas } from './ExtratoAsaas'
import { BankLogo } from './BankLogo'
import { TxnModal } from './TxnModal'
import { PanelShell, PlnBarra, PlnBarraSep, PlnBloco } from './PanelShell'
import {
  FiLoader, FiChevronLeft, FiChevronRight, FiRepeat, FiArrowUpCircle, FiArrowDownCircle,
  FiChevronDown, FiCreditCard, FiColumns, FiUploadCloud, FiSearch, FiX, FiRefreshCw,
} from 'react-icons/fi'

// Extrato de movimentações (PRD §5.8): consolidado de receitas+despesas+
// transferências com saldo corrente.

type MovType = 'receita' | 'despesa' | 'transferencia'
interface Account { id: number; name: string; is_pj_main: number; bank_code?: string | null; type?: string | null }
interface Item {
  type: MovType; id: string; date: string; description: string; subtitle: string | null
  amount: number; status: string; delta: number; balance: number
  // detalhes da linha (2026-07-20): logo do banco + favorecido/método/venc/origem
  account_id?: number | null; party?: string | null; method?: string | null
  ref?: string | null; due?: string | null
  from_account_id?: number | null; to_account_id?: number | null
}
const METHOD_LABEL: Record<string, string> = {
  pix: 'Pix', boleto: 'Boleto', dinheiro: 'Dinheiro', cartao: 'Cartão',
  transferencia: 'Transferência', debito: 'Débito', credito: 'Crédito',
}
// `transferencias` é o efeito LÍQUIDO das transferências no escopo filtrado —
// zero quando não há filtro de conta (as duas pontas se anulam). Sem ele a
// cascata não fecha: transferência mexe no saldo da conta sem ser receita nem
// despesa.
interface Cards { receitas_em_aberto: number; receitas_realizadas: number; despesas_em_aberto: number; despesas_realizadas: number; total: number; transferencias: number }
interface Data { month: string; opening: number; balance: number; cards: Cards; items: Item[] }

const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const ptDate = (s: string) => s.split('-').reverse().join('/')
const thisMonth = () => new Date().toISOString().slice(0, 7)
const monthLabel = (m: string) => {
  const [y, mo] = m.split('-').map(Number)
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}
const addMonth = (m: string, n: number) => {
  const [y, mo] = m.split('-').map(Number)
  const d = new Date(Date.UTC(y, mo - 1 + n, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}
const STATUS: Record<string, { label: string; cls: string }> = {
  vencido:       { label: 'Vencido',   cls: 'bg-red-500/10 text-red-600 dark:text-red-400' },
  vence_hoje:    { label: 'Vence hoje', cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  em_aberto:     { label: 'Em aberto', cls: 'bg-secondary text-muted-foreground' },
  agendado:      { label: 'Agendado',  cls: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  pago:          { label: 'Realizado', cls: 'bg-primary/10 text-primary' },
  transferencia: { label: 'Transferência', cls: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' },
}

// ── Prova real (de ↔ para) — pedido do diretor (2026-07-08): o extrato do
// banco importado (planilha, wizard da conciliação) vira uma SEGUNDA COLUNA
// deste extrato, linha a linha, pra conferir se o sistema bate com o banco —
// valor a valor e no fechamento do período. O pareamento vem pronto de
// /api/reconciliation/comparativo (conciliação real > mesmo valor com data a
// ≤3 dias > sobras) e casa com as linhas daqui pelos ids (t{id}/f{id}).
// Leitura apenas — dar baixa/conciliar é na aba Conciliação bancária.
interface CompLado {
  key?: string; date: string; amount: number
  memo?: string | null; status?: string
  description?: string; origem?: string
  id?: number   // lado banco: id do movimento importado (vincula a transferência à conciliação)
}
interface CompRow { banco: CompLado | null; sistema: CompLado | null; status: string; diffDias?: number; loteId?: string }
interface CompResumo {
  totalBanco: number; totalSistema: number; diferenca: number
  bancoLinhas: number; sistemaLinhas: number; counts: Record<string, number>
  lotes?: number
}
// Marcadores minimalistas (dot + rótulo, sem pílula): linha que bate gasta
// pouca tinta; cor de alerta fica reservada pra divergência — numa tabela
// densa é o que deixa o olho achar o problema de longe.
const PROVA_STATUS: Record<string, { label: string; dot: string; text: string }> = {
  conciliado: { label: 'bate',       dot: 'bg-emerald-500/80', text: 'text-muted-foreground' },
  provavel:   { label: 'bate',       dot: 'bg-emerald-500/80', text: 'text-muted-foreground' },
  lote:       { label: 'lote',       dot: 'bg-violet-500',     text: 'text-violet-600 dark:text-violet-400' },
  so_banco:   { label: 'só banco',   dot: 'bg-red-500',        text: 'text-red-600 dark:text-red-400' },
  so_sistema: { label: 'só sistema', dot: 'bg-sky-500',        text: 'text-sky-600 dark:text-sky-400' },
  ignorado:   { label: 'ignorado',   dot: 'bg-muted-foreground/40', text: 'text-muted-foreground' },
}
const ProvaMark = ({ st, title }: { st: { label: string; dot: string; text: string }; title?: string }) => (
  <span className={`inline-flex items-center gap-1.5 text-[10px] font-medium whitespace-nowrap ${st.text}`} title={title}>
    <span className={`w-1.5 h-1.5 rounded-full shrink-0 neu-dot ${st.dot}`} />{st.label}
  </span>
)

export function ExtratoMovimentacoes() {
  const { authenticatedFetch } = useAuth()
  const [data, setData] = useState<Data | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [month, setMonth] = useState(thisMonth())
  // Período personalizado DE/ATÉ — alternativa ao mês (a prova real compara
  // mês fechado, porque extrato de banco é mensal)
  const [periodMode, setPeriodMode] = useState<'mes' | 'custom'>('mes')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  // null = fechado; {} = novo em branco; com campos = pré-preenchido a partir
  // de uma linha do banco sem par na prova real (transferência não lançada)
  const [transferModal, setTransferModal] = useState<null | { fromId?: number; toId?: number; date?: string; amount?: number; description?: string; movementId?: number }>(null)
  const [txnModal, setTxnModal] = useState<null | { kind: 'receita' | 'despesa'; defaults: { description?: string; amount?: number; date?: string; bank_account_id?: number; paid?: boolean; from_bank_movement?: boolean } }>(null)

  // Prova real: segunda coluna com o extrato do banco (exige UMA conta + mês)
  const [provaReal, setProvaReal] = useState(false)
  const [comp, setComp] = useState<{ rows: CompRow[]; resumo: CompResumo } | null>(null)
  const [compLoading, setCompLoading] = useState(false)
  const [showImport, setShowImport] = useState(false)

  // Breakpoints em JS — necessários porque os cabeçalhos de GRUPO da prova
  // real usam colSpan, e colSpan não é responsivo por CSS: quando uma coluna
  // some (hidden lg/xl/2xl), o span precisa encolher junto senão o grupo
  // "Prova real" desliza pra cima das colunas do sistema.
  const [bp, setBp] = useState(() => ({
    sm: typeof window !== 'undefined' && window.innerWidth >= 640,
    lg: typeof window !== 'undefined' && window.innerWidth >= 1024,
    xl: typeof window !== 'undefined' && window.innerWidth >= 1280,
    xxl: typeof window !== 'undefined' && window.innerWidth >= 1536,
  }))
  useEffect(() => {
    const on = () => setBp({ sm: window.innerWidth >= 640, lg: window.innerWidth >= 1024, xl: window.innerWidth >= 1280, xxl: window.innerWidth >= 1536 })
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])

  // Filtro de contas (uma, várias ou todas) — mesma ideia do fluxo de caixa,
  // aqui multi-select: set vazio = "Todas as contas" (consolidado)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [pickerOpen, setPickerOpen] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    authenticatedFetch('/api/bank-accounts').then(r => r.json())
      .then(d => { if (d.success) setAccounts(d.items || []) }).catch(() => {})
  }, [authenticatedFetch])

  useEffect(() => {
    if (!pickerOpen) return
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [pickerOpen])

  // conta → logo/nome pras linhas do extrato
  const accMap = useMemo(() => {
    const m: Record<number, Account> = {}
    for (const a of accounts) m[a.id] = a
    return m
  }, [accounts])

  const toggleAccount = (id: number) => setSelected(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  const pickerLabel = selected.size === 0 ? 'Todas as contas'
    : selected.size === 1 ? (accounts.find(a => a.id === [...selected][0])?.name || '1 conta')
    : `${selected.size} contas`

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const acc = selected.size ? `&accounts=${[...selected].join(',')}` : ''
      const range = periodMode === 'custom' && fromDate && toDate ? `&from=${fromDate}&to=${toDate}` : ''
      const r = await authenticatedFetch(`/api/movimentacoes?month=${month}${acc}${range}`)
      const d = await r.json()
      if (d.success) setData(d)
      else setError(d.error || 'Falha ao carregar')
    } catch { setError('Erro de conexão') }
    finally { setLoading(false) }
  }, [authenticatedFetch, month, selected, periodMode, fromDate, toDate])
  useEffect(() => { load() }, [load])

  // Sync manual do Asaas — dispara o mesmo runSync do timer e recarrega o
  // extrato pra refletir títulos/baixas/taxas novos na hora.
  const [asaasSyncing, setAsaasSyncing] = useState(false)
  // Extrato do Asaas a lançar. O contador fica na etiqueta do botão porque, sem
  // ele, ninguém abre uma tela que "provavelmente está vazia" — e a taxa fica
  // esquecida no ledger, que é justamente o risco de tirar o lançamento
  // automático.
  const [extratoAsaas, setExtratoAsaas] = useState(false)
  const [asaasPend, setAsaasPend] = useState(0)
  const loadAsaasPend = useCallback(async () => {
    try {
      const r = await authenticatedFetch('/api/asaas/extrato')
      const d = await r.json()
      if (d?.success) setAsaasPend((d.itens || []).filter((i: { lancavel: boolean }) => i.lancavel).length)
    } catch { /* contador é acessório — falha em silêncio */ }
  }, [authenticatedFetch])
  useEffect(() => { loadAsaasPend() }, [loadAsaasPend])
  const syncAsaas = async () => {
    if (asaasSyncing) return
    setAsaasSyncing(true); setError(null)
    try {
      const r = await authenticatedFetch('/api/asaas/sync', { method: 'POST' })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || 'Falha no sync do Asaas')
      if (d.skipped) { window.alert('Um sync do Asaas já estava em andamento — aguarde alguns segundos e atualize.'); return }
      window.alert(`Sync Asaas concluído:\n· ${d.novos ?? 0} título(s) novo(s)\n· ${d.baixados ?? 0} baixado(s)\n· ${d.taxas ?? 0} taxa(s) lançada(s)${d.cancelados ? `\n· ${d.cancelados} cancelado(s)` : ''}${d.erros?.length ? `\n⚠ ${d.erros.length} erro(s) — ver logs do backend` : ''}\n\nSaques, se houver, esperam decisão em "Extrato Asaas".`)
      load(); loadAsaasPend()
    } catch (e: any) { setError(e.message) }
    finally { setAsaasSyncing(false) }
  }

  const compAccount = selected.size === 1 ? [...selected][0] : null
  const provaAtiva = provaReal && compAccount !== null && periodMode === 'mes'
  // As colunas do comparativo SÓ entram quando há extrato do banco importado
  // no mês — sem ele não há o que comparar, e marcar tudo "só sistema" é ruído
  // (feedback do dono: Sicredi sem import mostrava valores). provaAtiva segue
  // controlando o painel de aviso/CTA e o botão Importar.
  const provaTabela = provaAtiva && !!comp && comp.resumo.bancoLinhas > 0

  const loadComp = useCallback(async () => {
    if (!provaAtiva) { setComp(null); return }
    setCompLoading(true)
    try {
      const r = await authenticatedFetch(`/api/reconciliation/comparativo?account=${compAccount}&month=${month}`)
      const d = await r.json()
      if (d.success) setComp(d)
      else { setComp(null); setError(d.error || 'Falha ao carregar a prova real') }
    } catch { setComp(null); setError('Erro de conexão') }
    finally { setCompLoading(false) }
  }, [authenticatedFetch, provaAtiva, compAccount, month])
  useEffect(() => { loadComp() }, [loadComp])

  // Quando a conta escolhida não tem extrato importado no mês, aponta em qual
  // conta ele está (caso real: extrato da Caixa comparado na Sicredi mostra
  // "vazio" e o usuário acha que a prova real quebrou).
  const [hintOutraConta, setHintOutraConta] = useState<{ id: number; name: string } | null>(null)
  useEffect(() => {
    if (!provaAtiva || !comp || comp.resumo.bancoLinhas > 0) { setHintOutraConta(null); return }
    let cancelled = false
    ;(async () => {
      try {
        const r = await authenticatedFetch('/api/reconciliation/statements')
        const d = await r.json()
        if (cancelled || !d.success) return
        const ini = `${month}-01`, fim = `${month}-31`
        const s = (d.statements || []).find((st: any) =>
          st.bank_account_id && st.bank_account_id !== compAccount &&
          st.dtstart && st.dtend && st.dtstart <= fim && st.dtend >= ini)
        setHintOutraConta(s ? { id: s.bank_account_id, name: s.account_name || `conta ${s.bank_account_id}` } : null)
      } catch { /* dica é opcional */ }
    })()
    return () => { cancelled = true }
  }, [provaAtiva, comp, compAccount, month, authenticatedFetch])

  // Liga a prova real; sem UMA conta escolhida, auto-seleciona a PJ principal
  // (mesma lição do 1º uso real: sem conta única não há o que comparar).
  const toggleProvaReal = () => {
    if (provaReal) { setProvaReal(false); return }
    setProvaReal(true)
    if (selected.size !== 1) {
      const pj = accounts.find(a => a.is_pj_main)
      if (pj) setSelected(new Set([pj.id]))
    }
  }

  // Busca textual na tabela (descrição/subtítulo do sistema, histórico do
  // banco e valor). Com busca ativa a lista vira o resultado plano — lotes
  // são "explodidos" pra que membros que casem apareçam.
  const [busca, setBusca] = useState('')
  const normTxt = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

  // Lotes (rateios N:M detectados pelo backend — ex. descontos de líderes
  // pagos agregados): colapsados numa linha expansível por dia.
  const [expandedLotes, setExpandedLotes] = useState<Set<string>>(new Set())
  const toggleLote = (id: string) => setExpandedLotes(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  // Pareamento banco↔sistema indexado pelos ids do extrato (t{id}/f{id} = key
  // do lado sistema do comparativo) + linhas que só existem no banco.
  const prova = useMemo(() => {
    const bySys = new Map<string, CompRow>()
    const bancoOnly: CompRow[] = []
    if (provaTabela && comp) {
      for (const r of comp.rows) {
        const k = r.sistema?.key
        if (k) bySys.set(k, r)
        else if (r.banco) bancoOnly.push(r)
      }
      bancoOnly.sort((a, b) => (a.banco!.date < b.banco!.date ? -1 : 1))
    }
    return { bySys, bancoOnly }
  }, [comp, provaTabela])

  // Linhas exibidas: itens do sistema (ordem/saldo intactos) intercalados por
  // data com as linhas que só existem no extrato do banco. Com a prova real
  // ativa, cada linha ganha o SALDO DO BANCO acumulado (partindo da mesma
  // abertura) e o Δ (saldo sistema − saldo banco): o Δ fica parado enquanto os
  // dois lados batem e SALTA exatamente na linha que diverge — pedido do
  // diretor ("mostra a diferença mas não fala QUAL valor está diferente").
  const displayRows = useMemo(() => {
    const items = data?.items || []
    type DRow = { sys?: Item; comp?: CompRow; date: string; newDay: boolean; saldoBanco: number; delta: number; deltaChanged: boolean }
    const base: { sys?: Item; comp?: CompRow }[] = []
    if (!provaTabela) {
      for (const it of items) base.push({ sys: it })
    } else {
      // Movimentos de LOTE do banco não entram no interleave por data: são
      // inseridos logo APÓS o último lançamento do próprio lote. Sem isso o
      // agregado ia parar no fim do dia e as linhas vizinhas (não relacionadas)
      // exibiam um Δ transitório vermelho — o saldo do banco "atrasava" até o
      // agregado ser aplicado (bug real visto no lote de 10/02).
      const bancoSolo = prova.bancoOnly.filter(r => !r.loteId)
      const bancoLote = prova.bancoOnly.filter(r => r.loteId)
      let bi = 0
      for (const it of items) {
        while (bi < bancoSolo.length && bancoSolo[bi].banco!.date < it.date) base.push({ comp: bancoSolo[bi++] })
        base.push({ sys: it, comp: prova.bySys.get(it.id) })
      }
      while (bi < bancoSolo.length) base.push({ comp: bancoSolo[bi++] })
      // agrupa os movimentos de cada lote e injeta após o último membro-sistema
      const porLote = new Map<string, CompRow[]>()
      for (const r of bancoLote) {
        if (!porLote.has(r.loteId!)) porLote.set(r.loteId!, [])
        porLote.get(r.loteId!)!.push(r)
      }
      for (const [lid, movs] of porLote) {
        let pos = -1
        for (let i = 0; i < base.length; i++) if (base[i].comp?.loteId === lid) pos = i
        if (pos === -1) {
          // sem membro-sistema visível (não deveria ocorrer) — insere por data
          pos = base.findIndex(x => (x.sys?.date || x.comp?.banco?.date || '') > lid) - 1
          if (pos < -1) pos = base.length - 1
        }
        base.splice(pos + 1, 0, ...movs.map(m => ({ comp: m })))
      }
    }
    const r2 = (n: number) => Math.round(n * 100) / 100
    let runBank = 0
    let lastSysBal = data?.opening ?? 0
    let prevDelta = 0
    let prevDate = ''
    return base.map(r => {
      if (r.sys) lastSysBal = r.sys.balance
      if (r.comp?.banco) runBank = r2(runBank + r.comp.banco.amount)
      const saldoBanco = r2((data?.opening ?? 0) + runBank)
      const delta = r2(lastSysBal - saldoBanco)
      const date = r.sys?.date || r.comp?.banco?.date || ''
      const out: DRow = { ...r, date, newDay: date !== prevDate, saldoBanco, delta, deltaChanged: Math.abs(delta - prevDelta) >= 0.005 }
      prevDelta = delta
      prevDate = date
      return out
    })
  }, [data, prova, provaTabela])

  // Lista de renderização: membros de um lote colapsam numa linha-mãe
  // expansível (clique abre/fecha); fora de lote, a linha passa direto.
  type DRowOut = (typeof displayRows)[number] & { child?: boolean }
  const buscando = busca.trim().length > 0
  const renderRows = useMemo(() => {
    const out: Array<{ row?: DRowOut; lote?: { id: string; members: DRowOut[] } }> = []
    // Busca ativa → lista plana só com o que casa (inclusive dentro de lotes)
    if (buscando) {
      const q = normTxt(busca.trim())
      for (const r of displayRows) {
        const hit = normTxt(r.sys?.description || '').includes(q) ||
          normTxt(r.sys?.subtitle || '').includes(q) ||
          normTxt(r.comp?.banco?.memo || '').includes(q) ||
          String(r.sys?.amount ?? '').includes(q) ||
          String(r.comp?.banco?.amount ?? '').includes(q)
        if (hit) out.push({ row: r })
      }
      return out
    }
    if (!provaTabela) { for (const r of displayRows) out.push({ row: r }); return out }
    const seen = new Set<string>()
    for (const r of displayRows) {
      const lid = r.comp?.loteId
      if (!lid) { out.push({ row: r }); continue }
      if (seen.has(lid)) continue
      seen.add(lid)
      const members = displayRows.filter(x => x.comp?.loteId === lid)
      out.push({ lote: { id: lid, members } })
      if (expandedLotes.has(lid)) for (const m of members) out.push({ row: { ...m, child: true } })
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayRows, provaTabela, expandedLotes, busca, buscando])

  // Cascata começo → movimento → fim: Saldo inicial e Saldo final ancoram as
  // pontas; só as REALIZADAS entram no saldo. Em aberto (previsto) e resultado
  // do período viram legenda apagada embaixo — não competem com os âncoras.
  const negTone = 'text-red-600/90 dark:text-red-400/90'
  const isMes = periodMode === 'mes'
  // A cascata TEM que fechar: abertura + receitas − despesas + transferências =
  // saldo final. O card de transferência só aparece quando é diferente de zero
  // (sem filtro de conta ele é sempre zero e seria ruído) — e é justamente
  // quando ele existe que a conta sem ele não batia.
  const temTransf = !!data && Math.abs(data.cards.transferencias) >= 0.005
  const heroCards = data ? [
    { label: 'Saldo inicial', v: data.opening, sub: isMes ? 'começo do mês' : 'início do período', tone: data.opening < 0 ? negTone : 'text-foreground', anchor: true },
    { label: 'Receitas realizadas', v: data.cards.receitas_realizadas, sub: 'entra no saldo (+)', tone: 'text-primary', anchor: false },
    { label: 'Despesas realizadas', v: data.cards.despesas_realizadas, sub: 'sai do saldo (−)', tone: negTone, anchor: false },
    ...(temTransf ? [{
      label: 'Transferências',
      v: data.cards.transferencias,
      sub: data.cards.transferencias > 0 ? 'entrou de outra conta (+)' : 'saiu para outra conta (−)',
      tone: data.cards.transferencias < 0 ? negTone : 'text-primary', anchor: false,
    }] : []),
    { label: 'Saldo final', v: data.balance, sub: isMes ? '= início do próximo mês' : 'fim do período', tone: data.balance < 0 ? negTone : 'text-foreground', anchor: true },
  ] : []

  const nCols = provaTabela ? 10 : 5

  return (
    <PanelShell
      titulo="Extrato de movimentações"
      sub="Receitas, despesas e transferências num só extrato, com o saldo corrente"
      icone={<FiRepeat />}
      acoes={
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Sync manual do Asaas — o timer roda a cada 15 min; este botão é
              pro "gerei o boleto agora, cadê?" (sync é idempotente) */}
          <button onClick={syncAsaas} disabled={asaasSyncing}
            title="Buscar agora no Asaas os boletos emitidos/recebidos e taxas (o sistema também sincroniza sozinho a cada 15 min)"
            className="neu-convex inline-flex items-center gap-1.5 h-8 rounded-lg border border-border bg-card text-foreground text-xs font-medium px-2.5 hover:bg-secondary transition-colors disabled:opacity-50">
            {asaasSyncing ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiRefreshCw className="w-3.5 h-3.5" />}
            <span className="hidden md:inline">Sincronizar Asaas</span>
          </button>
          <button onClick={() => setExtratoAsaas(true)}
            title="Extrato do Asaas: escolher quais taxas viram lançamento (elas não entram mais sozinhas)"
            className="neu-convex inline-flex items-center gap-1.5 h-8 rounded-lg border border-border bg-card text-foreground text-xs font-medium px-2.5 hover:bg-secondary transition-colors">
            <FiCreditCard className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Extrato Asaas</span>
            {asaasPend > 0 && (
              <span className="neu-relief text-[11px] rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-400 px-1.5 font-semibold">{asaasPend}</span>
            )}
          </button>
          <button onClick={toggleProvaReal}
            disabled={periodMode === 'custom'}
            title={periodMode === 'custom'
              ? 'A prova real compara mês fechado — volte ao filtro por mês'
              : 'Prova real: mostra o extrato do banco importado como segunda coluna, linha a linha, pra conferir se o sistema bate com o banco'}
            className={`inline-flex items-center gap-1.5 h-8 rounded-lg border text-xs font-medium px-2.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${provaReal ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border bg-card text-foreground hover:bg-secondary'}`}>
            <FiColumns className="w-3.5 h-3.5" /> <span className="hidden md:inline">Prova real</span>
          </button>
          {provaAtiva && (
            <button onClick={() => setShowImport(true)} title="Importar extrato do banco (planilha XLS/XLSX/CSV) pra usar como prova real"
              className="neu-convex inline-flex items-center gap-1.5 h-8 rounded-lg border border-border bg-card text-foreground text-xs font-medium px-2.5 hover:bg-secondary transition-colors">
              <FiUploadCloud className="w-3.5 h-3.5" /> <span className="hidden md:inline">Importar extrato</span>
            </button>
          )}
          <button onClick={() => setTransferModal({})} title="Nova transferência entre contas"
            className="neu-relief inline-flex items-center gap-1.5 h-8 rounded-lg bg-primary text-primary-foreground text-xs font-medium px-2.5 hover:opacity-90 transition-opacity">
            <FiRepeat className="w-3.5 h-3.5" /> <span className="hidden md:inline">Nova transferência</span>
          </button>
        </div>
      }>
      {/* Barra única SÓ de controles (números moram no painel de indicadores):
          segmento Mês|De/Até (mesmo padrão do Contas a pagar e Fluxo), navegação
          do período, conta e ações — tudo h-8, nada de link miúdo sublinhado.
          PLANOS (21/10/2026): filtros numa PlnBarra; os botões de ação (Asaas,
          prova real, importar, transferência) subiram pro cabeçalho da tela. */}
      <PlnBarra>
        <div className="neu-seg inline-flex rounded-lg border border-border p-0.5 bg-card">
          <button onClick={() => setPeriodMode('mes')} title="Navegar por mês fechado"
            className={`px-2.5 h-7 text-xs rounded-md transition-colors ${periodMode === 'mes' ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>
            Mês
          </button>
          <button onClick={() => setPeriodMode('custom')} disabled={provaReal}
            title={provaReal ? 'A prova real compara mês fechado — desligue-a pra usar período livre' : 'Intervalo de datas livre (De/Até)'}
            className={`px-2.5 h-7 text-xs rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${periodMode === 'custom' ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>
            De/Até
          </button>
        </div>
        {periodMode === 'custom' ? (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input type="date" value={fromDate} max={toDate || undefined} onChange={e => setFromDate(e.target.value)}
              title="Início do período (data do movimento)"
              className="neu-convex h-8 rounded-lg border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
            <span>até</span>
            <input type="date" value={toDate} min={fromDate || undefined} onChange={e => setToDate(e.target.value)}
              title="Fim do período (data do movimento)"
              className="neu-convex h-8 rounded-lg border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <button onClick={() => setMonth(m => addMonth(m, -1))} title="Mês anterior" className="w-7 h-7 flex items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
              <FiChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="text-sm font-medium text-foreground capitalize min-w-[120px] text-center">{monthLabel(month)}</span>
            <button onClick={() => setMonth(m => addMonth(m, 1))} title="Próximo mês" className="w-7 h-7 flex items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
              <FiChevronRight className="w-3.5 h-3.5" />
            </button>
            {month !== thisMonth() && (
              <button onClick={() => setMonth(thisMonth())} title="Voltar ao mês atual"
                className="neu-convex ml-1 px-2 h-7 rounded-md border border-border text-[11px] font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
                Hoje
              </button>
            )}
          </div>
        )}

        <PlnBarraSep />
        {/* Seletor de contas (multi) */}
        <div className="relative" ref={pickerRef}>
          <button onClick={() => setPickerOpen(o => !o)} title="Filtrar por conta (uma ou várias)"
            className={`inline-flex items-center gap-2 h-10 rounded-lg border px-2.5 text-sm transition-colors ${selected.size ? 'border-primary/40 bg-primary/5 text-foreground' : 'border-border bg-card text-foreground hover:bg-secondary'}`}>
            {selected.size === 1 ? (
              // Conta única → logo do banco (mesmo SVG da tela de Contas)
              (() => { const a = accounts.find(x => x.id === [...selected][0]); return <BankLogo code={a?.bank_code} name={a?.name} type={a?.type} size={26} /> })()
            ) : (
              <FiCreditCard className={`w-4 h-4 ${selected.size ? 'text-primary' : 'text-muted-foreground'}`} />
            )}
            <span className="max-w-[220px] truncate font-medium">{pickerLabel}</span>
            <FiChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
          </button>
          {pickerOpen && (
            <div className="neu-pop absolute left-0 top-full mt-1 z-30 w-72 glass-panel rounded-xl border border-border shadow-lg p-2">
              <button onClick={() => setSelected(new Set())} title="Limpar filtro e voltar ao consolidado"
                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-sm transition-colors ${selected.size === 0 ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-secondary'}`}>
                Todas as contas
              </button>
              <div className="my-1.5 border-t border-border/60" />
              <div className="max-h-64 overflow-y-auto scrollbar-thin">
                {accounts.map(a => (
                  <label key={a.id} title={`Incluir/remover ${a.name} do extrato`}
                    className="neu-item flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-sm cursor-pointer hover:bg-secondary">
                    <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleAccount(a.id)}
                      className="neu-check w-4 h-4 accent-[hsl(var(--primary))] shrink-0" />
                    <BankLogo code={a.bank_code} name={a.name} type={a.type} size={22} />
                    <span className="truncate text-foreground">{a.name}{a.is_pj_main ? ' (PJ principal)' : ''}</span>
                  </label>
                ))}
                {accounts.length === 0 && (
                  <div className="px-2.5 py-2 text-xs text-muted-foreground">Nenhuma conta cadastrada.</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Busca — filtra a tabela ao digitar (descrição do sistema e
            histórico do banco); com busca ativa a lista vira o resultado. */}
        <div className="relative flex-1 min-w-[150px] max-w-xs">
          <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <input
            type="text" value={busca} onChange={e => setBusca(e.target.value)}
            placeholder="Buscar lançamento…"
            title="Filtra por descrição, histórico do banco ou valor (ex.: GRATIFICACAO)"
            className="w-full h-8 rounded-lg border border-border bg-background pl-8 pr-7 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
          {busca && (
            <button onClick={() => setBusca('')} title="Limpar busca"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <FiX className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </PlnBarra>

      {error && <div className="shrink-0 text-sm text-red-500">{error}</div>}

      {/* Painel de indicadores em CASCATA: Saldo inicial (começo) → Receitas
          realizadas (+) → Despesas realizadas (−) → [Transferências (±)] →
          Saldo final (fim, = começo do período seguinte). As pontas são âncoras
          (fundo sutil); só o realizado move o saldo. Em aberto (previsto) e
          resultado ficam numa legenda apagada logo abaixo, pra não competir com
          os âncoras. Quando a prova real está ativa, a linha do banco entra
          abaixo desta.

          O card de Transferências entrou em 03/08/2026: a cascata AFIRMA uma
          identidade (abertura + receitas − despesas = saldo final) que era
          falsa sempre que houvesse transferência com filtro de conta. Em
          julho/2026 a Caixinha mostrava abertura 2.541,05, despesas 1.260,96 e
          saldo final 10.080,09 — os R$ 8.800 do saque não apareciam em card
          nenhum. Só aparece quando é ≠ 0, que é exatamente quando faz falta. */}
      <div className="shrink-0 glass-panel neu-card rounded-xl border border-border overflow-hidden">
        <div className={`grid grid-cols-2 ${temTransf ? 'xl:grid-cols-5' : 'xl:grid-cols-4'} divide-x divide-y xl:divide-y-0 divide-border/60`}>
          {heroCards.map(c => (
            <div key={c.label} className={`px-4 py-3 min-w-0 ${c.anchor ? 'bg-secondary/40' : ''}`}
              title={c.label === 'Saldo inicial' ? 'Quanto havia em caixa no começo do período (é o saldo final do período anterior)'
                : c.label === 'Transferências' ? 'Efeito líquido das transferências entre contas próprias nesta conta. Move o saldo, mas não é receita nem despesa — por isso fica fora do resultado do período.'
                : c.label === 'Saldo final' ? 'Quanto ficou em caixa no fim do período — vira o saldo inicial do período seguinte. Só as realizadas entram; em aberto não.' : undefined}>
              {/* PLANOS: respiro nos cartões (rótulo 13, valor 18, pé 12) */}
              <div className="text-[13px] font-medium text-muted-foreground truncate">{c.label}</div>
              <div className={`mt-1 text-lg font-semibold tabular-nums whitespace-nowrap ${c.tone}`}>{data ? brl(c.v) : '—'}</div>
              <div className="text-xs text-muted-foreground/70 truncate">{c.sub}</div>
            </div>
          ))}
        </div>
        {data && (
          <div className="px-4 py-1.5 border-t border-border/60 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground/70">
            <span title="Lançamentos ainda não pagos — previsão. NÃO entram no saldo final.">
              Em aberto <span className="text-muted-foreground/50">(previsto — não entra no saldo)</span>:{' '}
              receitas <span className="tabular-nums font-medium">{brl(data.cards.receitas_em_aberto)}</span>{' '}
              · despesas <span className="tabular-nums font-medium">{brl(data.cards.despesas_em_aberto)}</span>
            </span>
            <span className="xl:ml-auto" title={`Receitas realizadas − despesas realizadas do período.${
              temTransf
                ? ' NÃO inclui transferência entre contas próprias: ela move o saldo mas não é lucro nem prejuízo, então aqui o resultado difere de Saldo final − Saldo inicial.'
                : ' Sem transferência no período, equivale a Saldo final − Saldo inicial.'}`}>
              Resultado do período: <span className={`tabular-nums font-medium ${data.cards.total < 0 ? negTone : 'text-foreground'}`}>{brl(data.cards.total)}</span>
            </span>
          </div>
        )}
      {provaAtiva && (
        <div className="border-t border-border/60">
          {compLoading ? (
            <div className="px-4 py-4 text-sm text-muted-foreground inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" /> Comparando com o extrato do banco…</div>
          ) : !comp ? (
            <div className="px-4 py-4 text-sm text-muted-foreground">Importe o extrato do banco deste mês (botão acima) pra comparar.</div>
          ) : comp.resumo.bancoLinhas === 0 && hintOutraConta ? (
            <div className="px-4 py-4 text-sm text-amber-600 dark:text-amber-400">
              Não há extrato importado deste mês nesta conta — mas há na conta <strong>{hintOutraConta.name}</strong>.{' '}
              <button onClick={() => setSelected(new Set([hintOutraConta.id]))} className="underline font-medium hover:opacity-80">Comparar nela</button>
              {' '}ou importe o extrato desta conta (botão acima).
            </div>
          ) : (() => {
            const fechamentoBanco = data ? Math.round((data.opening + comp.resumo.totalBanco) * 100) / 100 : 0
            const zerada = Math.abs(comp.resumo.diferenca) < 0.005
            const batem = (comp.resumo.counts.conciliado || 0) + (comp.resumo.counts.provavel || 0)
            // PLANOS: mesmo respiro dos cartões de cima (valor 16 aqui: são 6 por linha)
            const stat = 'px-4 py-3 min-w-0'
            const lbl = 'text-[13px] font-medium text-muted-foreground truncate'
            const val = 'mt-1 text-base font-semibold tabular-nums whitespace-nowrap'
            return (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 divide-x divide-y xl:divide-y-0 divide-border/60">
                <div className={stat} title="Soma do realizado do sistema nesta conta e mês (transações pagas + transferências)">
                  <div className={lbl}>Movimento · sistema</div>
                  <div className={`${val} text-foreground`}>{brl(comp.resumo.totalSistema)}</div>
                </div>
                <div className={stat} title="Soma dos movimentos do extrato importado do banco neste mês">
                  <div className={lbl}>Movimento · banco</div>
                  <div className={`${val} text-foreground`}>{brl(comp.resumo.totalBanco)}</div>
                </div>
                <div className={stat} title="Banco − Sistema. Zero = o mês fecha igual nos dois lados.">
                  <div className={lbl}>Diferença</div>
                  <div className={`${val} ${zerada ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{brl(comp.resumo.diferenca)}</div>
                </div>
                <div className={stat} title="Saldo final do mês pelo sistema × onde chegaria se o extrato do banco fosse a verdade (mesma abertura)">
                  <div className={lbl}>Saldo final · sist × banco</div>
                  <div className={`${val} text-foreground`}>
                    {data ? brl(data.balance) : '—'} <span className="text-muted-foreground font-normal">×</span> {brl(fechamentoBanco)}
                  </div>
                </div>
                <div className={stat} title="Rateios do dia com somas iguais dos dois lados (ex.: descontos de líderes pagos em lote) — linhas roxas expansíveis na tabela">
                  <div className={lbl}>Lotes rateados</div>
                  <div className={`${val} ${comp.resumo.lotes ? 'text-violet-600 dark:text-violet-400' : 'text-muted-foreground/60'}`}>
                    {comp.resumo.lotes || 0}{comp.resumo.counts.lote ? ` (${comp.resumo.counts.lote} itens)` : ''}
                  </div>
                </div>
                <div className={stat} title="Linhas pareadas × sem contrapartida em cada lado">
                  <div className={lbl}>Paridade</div>
                  <div className={`${val}`}>
                    <span className="text-emerald-600 dark:text-emerald-400">{batem} ✓</span>
                    <span className="text-muted-foreground font-normal"> · </span>
                    <span className={comp.resumo.counts.so_banco ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}>{comp.resumo.counts.so_banco || 0} banco</span>
                    <span className="text-muted-foreground font-normal"> · </span>
                    <span className={comp.resumo.counts.so_sistema ? 'text-sky-600 dark:text-sky-400' : 'text-muted-foreground'}>{comp.resumo.counts.so_sistema || 0} sist</span>
                  </div>
                </div>
              </div>
            )
          })()}
        </div>
      )}
      </div>

      {/* Tabela consolidada — com a prova real ativa, ganha as colunas do
          banco + Saldo banco (acumulado da mesma abertura) + Δ (sistema−banco).
          Tipografia compacta na prova (pedido do diretor: fonte menor, mais
          colunas, e apontar QUAL valor diverge — o Δ salta na linha culpada). */}
      {(() => {
        // Sombra proposital: dentro da TABELA, "prova ativa" = prova COM DADOS
        // (extrato do banco importado no mês). Sem dados, a tabela renderiza o
        // extrato normal — o painel de aviso acima é quem orienta a importar.
        const provaAtiva = provaTabela
        // Ritmo interno: px-3/py-2 entre colunas; variantes EXPLÍCITAS (sem
        // sobrepor px com pl — a ordem do CSS gerado decidiria o vencedor):
        // first/last dão 16px contra as bordas do painel, mid dá 16px depois
        // da divisória central — nada gruda em borda nenhuma.
        const cell = provaAtiva ? 'px-3 py-2' : 'px-4 py-2.5'
        const first = provaAtiva ? 'pl-4 pr-3 py-2' : cell
        const last = provaAtiva ? 'pl-3 pr-4 py-2' : cell
        const mid = provaAtiva ? 'pl-4 pr-3 py-2 border-l border-border' : `${cell} border-l border-border`
        const chip = provaAtiva ? 'text-[10px] px-1.5 py-px' : 'text-xs px-2 py-0.5'
        // Prioridade de colunas por largura de tela (as analíticas essenciais
        // nunca somem; o resto entra conforme couber): Situação ≥lg, Histórico
        // do banco ≥xl, Saldo banco ≥2xl. Os colSpans dos grupos acompanham
        // via bp (colSpan não é responsivo por CSS).
        const hideSitu = provaAtiva ? 'hidden lg:table-cell' : 'hidden sm:table-cell'
        const hideHist = 'hidden xl:table-cell'
        const hideSBanco = 'hidden 2xl:table-cell'
        const situVis = provaAtiva ? bp.lg : bp.sm
        const sysCols = 4 + (situVis ? 1 : 0)
        const bankCols = 3 + (bp.xl ? 1 : 0) + (bp.xxl ? 1 : 0)
        return (
      /* Única região com scroll; cabeçalho sticky no <thead> inteiro (a prova
         real tem 2 linhas de cabeçalho — sticky por célula exigiria offset). */
      <PlnBloco
        titulo="Movimentações"
        nota={buscando ? `${renderRows.length} resultado(s) para “${busca.trim()}”`
          : provaAtiva ? 'sistema à esquerda, extrato do banco à direita · o Δ salta na linha que diverge'
          : 'saldo corrente linha a linha, da abertura ao saldo final'}
        className="flex-1 min-h-[280px]"
      >
      <div className="neu-table glass-panel flex-1 min-h-0 overflow-auto scrollbar-thin">
        <table className={`w-full ${provaAtiva ? 'text-xs min-w-[760px] lg:min-w-[880px] xl:min-w-[1100px] 2xl:min-w-[1280px]' : 'text-sm min-w-[520px] sm:min-w-[720px]'}`}>
          <thead className="sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_hsl(var(--border))]">
            {provaAtiva && (
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground/80">
                <th colSpan={sysCols} className="pl-4 pr-3 pt-3 pb-0 text-left font-medium">Sistema</th>
                <th colSpan={bankCols} className="pl-4 pr-4 pt-3 pb-0 text-left font-medium border-l border-border">Prova real · extrato do banco</th>
              </tr>
            )}
            <tr className={`text-left ${provaAtiva ? 'text-[10px]' : 'text-xs'} text-muted-foreground`}>
              <th className={`${first} font-medium w-20`}>Data</th>
              <th className={`${cell} font-medium`}>Lançamento</th>
              <th className={`${cell} ${hideSitu} font-medium w-24`}>Situação</th>
              <th className={`${cell} font-medium w-28 text-right`}>Valor</th>
              <th className={`${cell} font-medium w-28 text-right`} title="Saldo corrente do sistema">Saldo</th>
              {provaAtiva && (
                <>
                  <th className={`${mid} font-medium w-24`} title="bate = conciliado ou mesmo valor com data próxima (≤3 dias)">Prova</th>
                  <th className={`${cell} ${hideHist} font-medium`} title="Linha correspondente do extrato importado do banco">Histórico</th>
                  <th className={`${cell} font-medium w-28 text-right`}>Valor</th>
                  <th className={`${cell} ${hideSBanco} font-medium w-28 text-right`} title="Saldo acumulado pelo extrato do banco, partindo da mesma abertura do sistema — compare com a coluna Saldo dia a dia">Saldo</th>
                  <th className={`${last} font-medium w-28 text-right`} title="Saldo do sistema − saldo do banco. Fica parado enquanto os dois lados batem e salta na linha que diverge (destacada)">Δ sist−banco</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={nCols} className="px-4 py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : !data || renderRows.length === 0 ? (
              <tr><td colSpan={nCols} className="px-4 py-10 text-center text-muted-foreground">
                {buscando ? `Nenhum lançamento encontrado para “${busca.trim()}”.`
                  : provaAtiva ? 'Nada neste mês — importe o extrato do banco e confira se há lançamentos no sistema.'
                  : 'Nenhuma movimentação neste mês.'}
              </td></tr>
            ) : (
              <>
                {!buscando && (
                <tr className="neu-band-1 border-b border-border/60 bg-secondary/20">
                  <td className={first} />
                  <td className={`${cell} text-muted-foreground`}>Saldo de abertura</td>
                  <td className={`${cell} ${hideSitu}`} />
                  <td className={cell} />
                  <td className={`${cell} text-right font-medium tabular-nums text-muted-foreground`}>{brl(data.opening)}</td>
                  {provaAtiva && (
                    <>
                      <td className={mid} />
                      <td className={`${cell} ${hideHist}`} />
                      <td className={`${cell} text-right font-medium tabular-nums text-muted-foreground`} title="O banco parte da mesma abertura">{brl(data.opening)}</td>
                      <td className={`${cell} ${hideSBanco}`} />
                      <td className={`${last} text-right tabular-nums text-muted-foreground/50`}>{brl(0)}</td>
                    </>
                  )}
                </tr>
                )}
                {renderRows.map((entry, idx) => {
                  // Linha-mãe de LOTE (rateio N:M do dia) — expansível no clique
                  if (entry.lote) {
                    const { id, members } = entry.lote
                    const sysM = members.filter(m => m.sys)
                    const bankM = members.filter(m => !m.sys)
                    const sysTotal = Math.round(sysM.reduce((t, m) => t + (m.comp?.sistema?.amount || 0), 0) * 100) / 100
                    const bankTotal = Math.round(bankM.reduce((t, m) => t + (m.comp?.banco?.amount || 0), 0) * 100) / 100
                    const ultimo = members[members.length - 1]
                    const ultimoSaldoSys = [...members].reverse().find(m => m.sys)?.sys?.balance
                    const open = expandedLotes.has(id)
                    const dayCls = members[0].newDay && idx > 0 ? 'border-t border-t-border' : ''
                    return (
                      <tr key={`lote-${id}`} onClick={() => toggleLote(id)}
                        title={open ? 'Recolher o lote' : 'Expandir o lote — mostra os lançamentos e os movimentos que o compõem'}
                        className={`border-b border-border/60 bg-violet-500/[0.04] hover:bg-violet-500/[0.08] cursor-pointer ${dayCls}`}>
                        <td className={`${first} text-foreground whitespace-nowrap font-medium tabular-nums`}>{ptDate(id)}</td>
                        <td className={cell} colSpan={situVis ? 2 : 1}>
                          <span className="inline-flex items-center gap-1.5">
                            {open ? <FiChevronDown className="w-3.5 h-3.5 text-violet-500 shrink-0" /> : <FiChevronRight className="w-3.5 h-3.5 text-violet-500 shrink-0" />}
                            <span className="font-medium text-foreground">Lote rateado</span>
                            <span className="text-muted-foreground truncate">{sysM.length} lançamento{sysM.length === 1 ? '' : 's'} ↔ {bankM.length} movimento{bankM.length === 1 ? '' : 's'} do banco</span>
                          </span>
                        </td>
                        <td className={`${cell} text-right font-medium whitespace-nowrap tabular-nums ${sysTotal < 0 ? 'text-red-600 dark:text-red-400' : 'text-primary'}`}>{brl(sysTotal)}</td>
                        <td className={`${cell} text-right whitespace-nowrap tabular-nums text-foreground`}>{ultimoSaldoSys != null ? brl(ultimoSaldoSys) : ''}</td>
                        <td className={mid}><ProvaMark st={PROVA_STATUS.lote} title="Somas iguais dos dois lados no dia — rateio pago agregado (ex.: descontos de líderes)" /></td>
                        <td className={`${cell} ${hideHist} text-muted-foreground`}>{bankM.length} movimento{bankM.length === 1 ? '' : 's'} agregado{bankM.length === 1 ? '' : 's'}</td>
                        <td className={`${cell} text-right font-medium whitespace-nowrap tabular-nums text-foreground`}>{brl(bankTotal)}</td>
                        <td className={`${cell} ${hideSBanco} text-right whitespace-nowrap tabular-nums text-foreground`}>{brl(ultimo.saldoBanco)}</td>
                        <td className={`${last} text-right whitespace-nowrap tabular-nums ${Math.abs(ultimo.delta) < 0.005 ? 'text-muted-foreground/50' : 'text-red-600 dark:text-red-400'}`}>{brl(ultimo.delta)}</td>
                      </tr>
                    )
                  }
                  const row = entry.row!
                  // Δ: quase sem tinta enquanto zerado; vermelho quando aberto;
                  // a célula onde o valor MUDA (linha culpada) ganha destaque.
                  // Filhos de lote NÃO destacam: o Δ oscila dentro do lote por
                  // construção (sistema e banco descem em ordens diferentes) e
                  // volta a zero no fim — não é divergência.
                  const dZero = Math.abs(row.delta) < 0.005
                  const dCls = row.child ? 'text-muted-foreground/40' : dZero ? 'text-muted-foreground/50' : 'text-red-600 dark:text-red-400 font-medium'
                  const dMark = row.deltaChanged && !row.child ? 'font-bold bg-amber-500/15 rounded-sm' : ''
                  const dayCls = row.newDay && idx > 0 ? 'border-t border-t-border' : ''
                  // Linha que só existe no extrato do banco — intercalada por data
                  if (!row.sys) {
                    const b = row.comp!.banco!
                    const st = PROVA_STATUS[row.comp!.status] || PROVA_STATUS.so_banco
                    const isChild = !!row.child
                    return (
                      <tr key={`b-${idx}`} className={`border-b border-border/60 last:border-0 ${isChild ? 'bg-violet-500/[0.02]' : 'bg-red-500/[0.035] hover:bg-red-500/[0.07]'} ${isChild ? '' : dayCls}`}>
                        <td className={`${first} text-foreground whitespace-nowrap font-medium tabular-nums`}>{row.newDay && !isChild ? ptDate(b.date) : ''}</td>
                        <td className={`${cell} text-muted-foreground`} colSpan={situVis ? 4 : 3}>
                          {isChild ? (
                            <span className="text-muted-foreground/50 pl-5">movimento agregado do lote →</span>
                          ) : (
                          <span className="flex items-center justify-between gap-3">
                            <span className="text-muted-foreground/70 truncate">sem lançamento correspondente no sistema</span>
                            <span className="inline-flex items-center gap-1.5 shrink-0">
                              {/* lançamento certo pelo contexto: crédito → receita, débito →
                                  despesa (transferência não serve quando a contraparte é
                                  externa — caso real: PIX recebido travava no origem=destino) */}
                              <button
                                onClick={() => setTxnModal({
                                  kind: b.amount > 0 ? 'receita' : 'despesa',
                                  defaults: { description: b.memo || '', amount: Math.abs(b.amount), date: b.date, bank_account_id: compAccount!, paid: true, from_bank_movement: true },
                                })}
                                title={b.amount > 0
                                  ? 'Lançar como receita recebida — descrição, valor, data e conta já preenchidos; escolha só a categoria'
                                  : 'Lançar como despesa paga — descrição, valor, data e conta já preenchidos; escolha só a categoria'}
                                className="neu-convex inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-[10px] font-medium text-foreground hover:bg-secondary transition-colors">
                                {b.amount > 0
                                  ? <FiArrowUpCircle className="w-3 h-3 text-emerald-500" />
                                  : <FiArrowDownCircle className="w-3 h-3 text-red-400" />}
                                <span className="hidden sm:inline">lançar {b.amount > 0 ? 'receita' : 'despesa'}</span><span className="sm:hidden">lançar</span>
                              </button>
                              <button
                                onClick={() => setTransferModal({
                                  ...(b.amount < 0 ? { fromId: compAccount! } : { toId: compAccount! }),
                                  date: b.date,
                                  amount: Math.abs(b.amount),
                                  description: b.memo || 'Transferência (extrato do banco)',
                                  // vincula à conciliação: o movimento sai da fila de
                                  // pendentes em vez de continuar pedindo "Criar título"
                                  movementId: b.id,
                                })}
                                title="Lançar como transferência entre as SUAS contas (ex.: saque pra caixinha, PIX entre contas próprias) — escolha só a conta de contrapartida"
                                className="neu-convex inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-[10px] font-medium text-foreground hover:bg-secondary transition-colors">
                                <FiRepeat className="w-3 h-3 text-violet-500" /> <span className="hidden md:inline">transferência</span>
                              </button>
                            </span>
                          </span>
                          )}
                        </td>
                        <td className={mid}><ProvaMark st={st} /></td>
                        <td className={`${cell} ${hideHist}`}><div className="text-foreground truncate max-w-[240px]">{b.memo || '(sem histórico)'}</div></td>
                        <td className={`${cell} text-right whitespace-nowrap tabular-nums text-foreground`}>{brl(b.amount)}</td>
                        <td className={`${cell} ${hideSBanco} text-right whitespace-nowrap tabular-nums text-foreground`}>{brl(row.saldoBanco)}</td>
                        <td className={`${last} text-right whitespace-nowrap tabular-nums ${dCls} ${dMark}`}>{brl(row.delta)}</td>
                      </tr>
                    )
                  }
                  const it = row.sys
                  const st = STATUS[it.status] || STATUS.em_aberto
                  const Icon = it.type === 'receita' ? FiArrowDownCircle : it.type === 'despesa' ? FiArrowUpCircle : FiRepeat
                  const iconCls = it.type === 'receita' ? 'text-primary' : it.type === 'despesa' ? 'text-red-500' : 'text-violet-500'
                  const valCls = it.delta > 0 ? 'text-primary' : it.delta < 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'
                  const sign = it.delta > 0 ? '+' : it.delta < 0 ? '−' : ''
                  // Lado banco: pareado → mostra; realizado sem par → "só no
                  // sistema"; não realizado → fora da prova (banco só tem caixa).
                  const realizado = it.status === 'pago' || it.type === 'transferencia'
                  const pst = row.comp ? (PROVA_STATUS[row.comp.status] || PROVA_STATUS.so_sistema) : realizado ? PROVA_STATUS.so_sistema : null
                  return (
                    <tr key={it.id} className={`border-b border-border/60 last:border-0 hover:bg-secondary/40 ${row.child ? 'bg-violet-500/[0.02]' : dayCls}`}>
                      <td className={`${first} text-foreground whitespace-nowrap font-medium tabular-nums`}>{!provaAtiva || (row.newDay && !row.child) ? ptDate(it.date) : ''}</td>
                      {/* w-full max-w-0: a célula ocupa o espaço restante e TRUNCA —
                          sem isso, descrição longa estica a tabela e corta o Saldo */}
                      <td className={`${cell} w-full max-w-0`}>
                        <div className={`flex items-center ${provaAtiva ? 'gap-1.5' : 'gap-2'}`}>
                          {/* logo da conta do movimento (transferência: origem → destino) */}
                          {it.type === 'transferencia' ? (
                            <>
                              {it.from_account_id && accMap[it.from_account_id] && (
                                <span title={`Origem: ${accMap[it.from_account_id].name}`} className="shrink-0 inline-flex">
                                  <BankLogo code={accMap[it.from_account_id].bank_code} name={accMap[it.from_account_id].name} type={accMap[it.from_account_id].type} size={provaAtiva ? 14 : 22} />
                                </span>
                              )}
                              <Icon className={`${provaAtiva ? 'w-3 h-3' : 'w-4 h-4'} shrink-0 ${iconCls}`} />
                              {it.to_account_id && accMap[it.to_account_id] && (
                                <span title={`Destino: ${accMap[it.to_account_id].name}`} className="shrink-0 inline-flex">
                                  <BankLogo code={accMap[it.to_account_id].bank_code} name={accMap[it.to_account_id].name} type={accMap[it.to_account_id].type} size={provaAtiva ? 14 : 22} />
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              {it.account_id && accMap[it.account_id] && (
                                <span title={`Conta: ${accMap[it.account_id].name}`} className="shrink-0 inline-flex">
                                  <BankLogo code={accMap[it.account_id].bank_code} name={accMap[it.account_id].name} type={accMap[it.account_id].type} size={provaAtiva ? 14 : 22} />
                                </span>
                              )}
                              <Icon className={`${provaAtiva ? 'w-3 h-3' : 'w-4 h-4'} shrink-0 ${iconCls}`} />
                            </>
                          )}
                          <div className="min-w-0">
                            <div className="text-foreground truncate">
                              {it.description}
                              {it.ref === 'ASAAS' && <span className="neu-relief ml-1.5 align-middle text-[9px] font-semibold rounded px-1 py-px bg-sky-500/15 text-sky-600 dark:text-sky-400" title="Boleto de negociação emitido no Asaas (entra pela sincronização)">ASAAS</span>}
                            </div>
                            {(() => {
                              // linha de detalhes: categoria · favorecido · método · venc (quando pago fora do vencimento)
                              const partes = [
                                it.subtitle,
                                it.party,
                                it.method ? (METHOD_LABEL[it.method] || it.method) : null,
                                it.status === 'pago' && it.due && it.due !== it.date ? `venc ${ptDate(it.due)}` : null,
                              ].filter(Boolean)
                              return partes.length > 0 && (
                                <div className={`${provaAtiva ? 'text-[10px]' : 'text-[11px]'} text-muted-foreground truncate`} title={partes.join(' · ')}>
                                  {partes.join(' · ')}
                                </div>
                              )
                            })()}
                          </div>
                        </div>
                      </td>
                      <td className={`${cell} ${hideSitu}`}>
                        {provaAtiva
                          ? <span className="text-[10px] font-medium text-muted-foreground">{st.label}</span>
                          : <span className={`font-medium rounded-full ${chip} ${st.cls}`}>{st.label}</span>}
                      </td>
                      <td className={`${cell} text-right font-medium whitespace-nowrap tabular-nums ${valCls}`}>{sign}{brl(it.amount)}</td>
                      <td className={`${cell} text-right whitespace-nowrap tabular-nums text-foreground`}>{brl(it.balance)}</td>
                      {provaAtiva && (
                        <>
                          <td className={mid}>
                            {pst ? (
                              <ProvaMark st={pst}
                                title={row.comp?.status === 'provavel' && row.comp?.diffDias ? `Mesmo valor, ${row.comp.diffDias} dia${row.comp.diffDias === 1 ? '' : 's'} de diferença na data` : row.comp?.status === 'so_sistema' ? 'Realizado no sistema sem linha correspondente no extrato do banco' : undefined} />
                            ) : (
                              <span className="text-muted-foreground/40" title="Título não realizado — só o caixa entra na prova real">·</span>
                            )}
                          </td>
                          <td className={`${cell} ${hideHist}`}>
                            {row.comp?.banco ? (
                              <div className="min-w-0 max-w-[240px]">
                                <div className="text-foreground truncate">{row.comp.banco.memo || '(sem histórico)'}</div>
                                {row.comp.banco.date !== it.date && <div className="text-[10px] text-muted-foreground tabular-nums">{ptDate(row.comp.banco.date)}</div>}
                              </div>
                            ) : <span className="text-muted-foreground/40">·</span>}
                          </td>
                          <td className={`${cell} text-right whitespace-nowrap tabular-nums ${row.comp?.banco ? 'text-foreground' : ''}`}>
                            {row.comp?.banco ? brl(row.comp.banco.amount) : ''}
                          </td>
                          <td className={`${cell} ${hideSBanco} text-right whitespace-nowrap tabular-nums ${row.comp?.banco ? 'text-foreground' : 'text-muted-foreground/40'}`}>{brl(row.saldoBanco)}</td>
                          <td className={`${last} text-right whitespace-nowrap tabular-nums ${dCls} ${dMark}`}>{brl(row.delta)}</td>
                        </>
                      )}
                    </tr>
                  )
                })}
                {!buscando && provaAtiva && comp && data && (() => {
                  const fechBanco = Math.round((data.opening + comp.resumo.totalBanco) * 100) / 100
                  const fechou = Math.abs(comp.resumo.diferenca) < 0.005
                  return (
                    <tr className="bg-secondary/30 border-t border-t-border">
                      <td className={first} />
                      <td className={`${cell} font-medium text-muted-foreground`} colSpan={situVis ? 2 : 1}>Fechamento do período</td>
                      <td className={cell} />
                      <td className={`${cell} text-right font-semibold tabular-nums text-foreground`}>{brl(data.balance)}</td>
                      <td className={mid}>
                        <ProvaMark st={fechou ? PROVA_STATUS.conciliado : PROVA_STATUS.so_banco} title={fechou ? 'Os dois lados fecham igual' : 'Sistema e banco fecham diferente — o Δ mostra quanto'} />
                      </td>
                      <td className={`${cell} ${hideHist} text-muted-foreground`}>saldo final se o banco fosse a verdade →</td>
                      <td className={`${cell} text-right font-semibold tabular-nums text-foreground`} title="Saldo final se o extrato do banco fosse a verdade (abertura + movimento do banco)">{brl(fechBanco)}</td>
                      <td className={`${cell} ${hideSBanco}`} />
                      <td className={`${last} text-right font-semibold tabular-nums ${fechou ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {brl(Math.round((data.balance - fechBanco) * 100) / 100)}
                      </td>
                    </tr>
                  )
                })()}
              </>
            )}
          </tbody>
        </table>
      </div>
      </PlnBloco>
        )
      })()}

      {transferModal && (
        <NovaTransferencia
          defaultFromId={transferModal.fromId}
          defaultToId={transferModal.toId}
          defaultDate={transferModal.date}
          defaultAmount={transferModal.amount}
          defaultDescription={transferModal.description}
          movementId={transferModal.movementId}
          onClose={() => setTransferModal(null)}
          onSaved={() => { setTransferModal(null); load(); loadComp() }} />
      )}
      {txnModal && (
        <TxnModal
          kind={txnModal.kind}
          defaults={txnModal.defaults}
          onClose={() => setTxnModal(null)}
          onSaved={() => { setTxnModal(null); load(); loadComp() }} />
      )}

      {/* Wizard de import (o mesmo da Conciliação bancária) — sem auto-criação
          de títulos (prova real só compara); ao terminar, recarrega tudo. */}
      {showImport && (
        <ImportarExtratoCsv
          authenticatedFetch={authenticatedFetch}
          skipAutoCreate
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); loadComp(); load() }}
        />
      )}

      {extratoAsaas && (
        <ExtratoAsaas
          onClose={() => { setExtratoAsaas(false); loadAsaasPend() }}
          onLancado={() => { load(); loadAsaasPend() }}
          onLancarManual={l => {
            // Saque do Asaas: abre o lançamento normal já preenchido. Fica como
            // DESPESA porque valor negativo é saída, mas a pessoa troca a
            // categoria e a natureza na tela — a decisão é dela, o sistema só
            // para de esconder que existe algo a lançar.
            setExtratoAsaas(false)
            setTxnModal({ kind: 'despesa', defaults: {
              description: l.description || 'Saque Asaas',
              amount: Math.abs(l.valor), date: l.tx_date, paid: true,
              bank_account_id: accounts.find(a => a.name === 'Asaas')?.id,
            } })
          }}
        />
      )}
    </PanelShell>
  )
}
